// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {css, StyleSheet} from 'aphrodite';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';
import {unmountComponentAtNode} from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import {ComponentsSequence} from 'components/Sequence';
import * as context from 'Context';
import Protein from 'molbio/aa-sequences/AaSequence';
import BiointelligentLinkComponentInfoBanner from 'molbio/biointelligent-links/BiointelligentLinkComponentInfoBanner';
import {runDuplicatePolymerModal} from 'molbio/components/DuplicatePolymerModal';
import * as infinitescroller from 'molbio/maps/linear/InfiniteScroller';
import OligoPropertiesBanner from 'molbio/maps/linear/OligoPropertiesBanner';
import PolymerSliceItemSource from 'molbio/maps/linear/PolymerSliceItemSource';
import Slices from 'molbio/maps/linear/Slices';
import {SliceView} from 'molbio/maps/linear/SliceView';
import {PolymerLinearMapState} from 'molbio/maps/MapStates';
import PartsWarningBanner from 'molbio/maps/PartsWarningBanner';
import PolymerMapTabView from 'molbio/maps/PolymerMapTabView';
import {Selection} from 'molbio/maps/selectables/Selection';
import Type from 'molbio/maps/Type';
import ModifiedNucleotidesLegend from 'molbio/monomers/ModifiedNucleotidesLegend';
import SinglePolymerMapTabState from 'molbio/tabstates/SinglePolymerMapTabState';
import * as Perf from 'performance/Perf';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {isDownKey, isLeftKey, isRightKey, isUpKey} from 'util/Keyboard';
import {MouseWheelEvent} from 'util/Mouse';
import {ElementWheelEvent} from 'util/Types';

interface MergedPolymerInterfaces {
  get(attr: 'selection'): Selection;
}

/**
 * An abstract view class for rendering a virtualized linear polymer map across multiple slices.
 */
export default abstract class PolymerLinearMapView<
  Polymer extends ComponentsSequence | Protein,
  MapState extends PolymerLinearMapState<Polymer>,
  TabState extends SinglePolymerMapTabState<Polymer, MapState>,
> extends PolymerMapTabView<TabState> {
  infiniteScroller!: infinitescroller.InfiniteScroller<SliceView>;
  sliceSource!: PolymerSliceItemSource<Polymer, MapState, TabState>;
  infiniteScrollbar!: infinitescroller.InfiniteScrollbar;
  slices!: Slices;
  type!: Type;
  mapType!: string;
  mapstate!: MapState;
  mapLoadPerfId!: Perf.PerfId;

  override initialize(options: Backbone.ViewOptions<TabState>): void {
    super.initialize(options);
    this.mapLoadPerfId = Perf.start('loadLinearMap', {enabled: false});
    this.slices = new Slices();
    this.mapstate = this.model.get('mapstate');
    this.type = this.getType();
    this.mapType = 'linear';

    this.sliceSource = this.createSliceItemSource({
      mapstate: this.mapstate,
      tabstate: this.model,
      $linear: this.$el,
      cutsiteSlices: this.slices,
    });
    const scrollData = this.mapstate.get('scrollData');
    this.infiniteScroller = new infinitescroller.InfiniteScroller({
      tabstate: this.model,
      mapstate: this.mapstate,
      itemSource: this.sliceSource,
      scrollData,
      spacing: 30,
    });
    this.infiniteScrollbar = new infinitescroller.InfiniteScrollbar({
      tabstate: this.model,
      model: scrollData,
      // infiniteScroller.$el.height() height is affected by:
      // - bufferViewLayout.get('height') which is already subscribed to via PolymerSliceItemSource.initialize
      // - `sequence.isRNASequence()` which is a static value for a given sequence.
      fullHeight: () => this.infiniteScroller.$el.height(),
    });
    this.sliceSource.setScroller(this.infiniteScroller);
  }

  abstract getType(): Type;

  /**
   * Create an item source instance to power the infinite scroller virtualizer.
   */
  abstract createSliceItemSource({
    mapstate,
    tabstate,
    $linear,
    cutsiteSlices,
  }: {
    mapstate: MapState;
    tabstate: TabState;
    $linear: JQuery;
    cutsiteSlices: Slices;
  }): PolymerSliceItemSource<Polymer, MapState, TabState>;

  handleArrowPress(e: JQueryEventObject): void {
    e.preventDefault();
    const mergedModel: MergedPolymerInterfaces = this.mapstate.polymer();
    const selection = mergedModel.get('selection');
    const shift = e.shiftKey;
    const maxBases = this.mapstate.get('maxBases');
    const start = selection.start();
    const end = selection.end();
    const moveEnd = this.getMoveEnd();
    let currentCol = selection.getState('column');

    if (currentCol == null) {
      selection.addState({column: moveEnd % maxBases});
      currentCol = moveEnd % maxBases;
    }

    if (isLeftKey(e)) {
      if (shift) {
        selection.linearExtendBy(-1);
      } else if (selection.isEmpty()) {
        selection.resetBy(-1);
      } else {
        selection.reset(start);
      }
      selection.addState({column: this.getMoveEnd() % maxBases});
    } else if (isRightKey(e)) {
      if (shift) {
        selection.linearExtendBy(1);
      } else if (selection.isEmpty()) {
        selection.resetBy(1);
      } else {
        selection.reset(end);
      }
      selection.addState({column: this.getMoveEnd() % maxBases});
    } else if (isUpKey(e)) {
      if (shift) {
        selection.linearExtendByRow(-1, maxBases, currentCol);
      } else {
        selection.resetByRow(-1, maxBases, currentCol);
      }
      selection.addState({column: currentCol});
    } else if (isDownKey(e)) {
      if (shift) {
        selection.linearExtendByRow(1, maxBases, currentCol);
      } else {
        selection.resetByRow(1, maxBases, currentCol);
      }
      selection.addState({column: currentCol});
    }

    this.mapstate.polymer().trigger('cursorJump');
  }

  scrollToIncludeCursor(): void {
    const currentRow = Math.max(0, Math.floor((this.getMoveEnd() - 1) / this.mapstate.get('maxBases')));
    this.infiniteScroller.scrollToIncludeIndex(currentRow);
  }

  override remove(): this {
    this.infiniteScroller.remove();
    this.infiniteScrollbar.remove();
    this.sliceSource.stopListening();
    $(window).off('.linear');
    if (this.$('#parts-warning-banner').children().length > 0) {
      unmountComponentAtNode(this.$('#parts-warning-banner')[0]);
    }
    if (this.$('#oligo-properties').children().length > 0) {
      unmountComponentAtNode(this.$('#oligo-properties')[0]);
    }
    super.remove();
    return this;
  }

  handleJump(residueIndex: number): void {
    if (context.get('mapId') !== this.mapstate.cid) {
      const sliceIndex = this.sliceSource.indexOf(residueIndex);
      this.infiniteScroller.scrollToIncludeIndex(sliceIndex);
    }
  }

  override registerListeners(): void {
    this.listenTo(this.mapstate, 'change:skipRender', () => {
      this.handleChangeSkipRender();
    });
    this.listenTo(this.mapstate.polymer(), 'jump', (residueIndex: number) => {
      this.handleJump(residueIndex);
    });
    this.listenTo(this.mapstate.polymer(), 'cursorJump', () => {
      this.scrollToIncludeCursor();
    });
    this.listenTo(this.infiniteScroller, 'scroll', (lastMouseX: number, lastMouseY: number) => {
      this.handleScroll(lastMouseX, lastMouseY);
    });
    // Cutsites need to be recomputed on resizeX / zoom change.
    // @ts-expect-error: only the sequence map view defines this method
    this.listenTo(this.sliceSource, 'resizeX', this.debouncedUpdateCutsiteData);
    $(window).on('mouseup.linear', () => {
      this.updateSelection();
    });

    // We only add listeners for the oligo properties tab for oligos with the flag enabled, and do not add
    // listeners for oligo properties for complex polymers.
    const polymer = this.model.get('sequence');
    if (polymer instanceof ComponentsSequence && polymer.isOligo()) {
      const oligoPropertiesVisibilityOption = this.mapstate
        .get('visibilityOptions')
        .getOption(vo.OLIGO_PROPERTIES.name)!;
      this.listenTo(oligoPropertiesVisibilityOption, 'change:visible', () => {
        if (oligoPropertiesVisibilityOption.get('visible')) {
          renderToDOM(
            <OligoPropertiesBanner sequenceId={polymer.get('id')} />,
            this.$('#oligo-properties')[0]
          );
        } else {
          unmountComponentAtNode(this.$('#oligo-properties')[0]);
        }
      });
    }

    d3.select(this.el).on(
      `${MouseWheelEvent}.linear`,
      (e: ElementWheelEvent & {wheelDelta?: number}): void => {
        const r = e.wheelDelta
          ? e.wheelDelta
          : e.detail
            ? e.detail * -40
            : e && e.wheelDelta
              ? e.wheelDelta
              : 0;

        if (e.ctrlKey) {
          if (r < 0) {
            this.mapstate.changeZoomByStep(Math.floor(r / 120 / 2) * 2);
          } else {
            this.mapstate.changeZoomByStep(Math.ceil(r / 120 / 2) * 2);
          }
          e.preventDefault();
        }
      }
    );
  }

  updateSelection(): void {
    const mergedModel: MergedPolymerInterfaces = this.model.polymer();
    const selection = mergedModel.get('selection');
    selection.set({active: null});
  }

  maxBases(): number {
    return this.mapstate.get('maxBases');
  }

  handleScroll(lastMouseX: number, lastMouseY: number): void {
    // If new slices are visible, compute cutsites.

    // Adjust the selection if we need to.
    const mergedModels: MergedPolymerInterfaces = this.mapstate.polymer();
    if (mergedModels.get('selection').get('active') && lastMouseY != null) {
      const sliceView = this.infiniteScroller.findSliceByTop(lastMouseY);
      sliceView.updateSelectionFromScroll(lastMouseX);
      document.getSelection()!.removeAllRanges();
    }

    // Reposition the input bubble
    this.repositionBubble();
  }

  handleChangeSkipRender(): void {
    this.$('.scroll-wrapper').toggleClass('hidden', this.mapstate.get('skipRender'));
  }

  override render(): this {
    Perf.markStart(this.mapLoadPerfId);
    super.render();
    this.registerListeners();
    const polymer = this.model.get('sequence');
    const $banner = $('<div id="parts-warning-banner"></div>');
    if (
      polymer instanceof ComponentsSequence &&
      polymer.get('writable') &&
      !BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS
    ) {
      this.$el.prepend($banner);
      renderToDOM(<PartsWarningBanner sequenceId={polymer.get('id')} />, $banner[0]);
    } else if (
      polymer instanceof Protein &&
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS &&
      BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS &&
      polymer.writable()
    ) {
      this.$el.prepend($banner);
      renderToDOM(
        <div style={{margin: '4px'}}>
          <BiointelligentLinkComponentInfoBanner
            polymerId={polymer.id}
            onDuplicatePolymer={async () => await runDuplicatePolymerModal(polymer, polymer.tagSchemaId())}
          />
        </div>,
        $banner[0]
      );
    }
    // We only render the oligo properties tab for oligos with the flag enabled, and do not render oligo
    // properties for complex polymers.
    if (polymer instanceof ComponentsSequence && polymer.isOligo()) {
      const $banner = $('<div id="oligo-properties"></div>');
      this.$el.prepend($banner);
      const oligoPropertiesVisibilityOption = this.mapstate
        .get('visibilityOptions')
        .getOption(vo.OLIGO_PROPERTIES.name)!;
      if (oligoPropertiesVisibilityOption.get('visible')) {
        renderToDOM(<OligoPropertiesBanner sequenceId={polymer.get('id')} />, $banner[0]);
      }
    }
    const $scrollWrapper = $('<div class="reset scroll-wrapper"></div>');
    this.$el.addClass(css(styles.linearMap));
    this.$el.append($scrollWrapper);
    this.handleChangeSkipRender();
    $scrollWrapper.append(this.infiniteScroller.render().el);
    $scrollWrapper.append(this.infiniteScrollbar.render().el);
    if (polymer instanceof ComponentsSequence && polymer.supportsChemicalMods()) {
      this.$el.addClass(css(styles.linearMapWithLegend));
      const $legend = $('<div id="modified-nucleotides-legend"></div>');
      $legend.addClass(css(styles.legend));
      this.$el.append($legend);
      const renderLegend = (): void => {
        renderToDOM(<ModifiedNucleotidesLegend sequenceId={polymer.get('id')} />, $legend[0]);
      };

      this.listenTo(polymer, 'splitWorkspace', () => {
        renderLegend();
      });
      renderLegend();
    }
    Perf.markEnd(this.mapLoadPerfId);
    return this;
  }
}

const styles = StyleSheet.create({
  linearMap: {
    overflow: 'hidden',
  },
  linearMapWithLegend: {
    display: 'flex',
    flexDirection: 'column',
  },
  legend: {
    display: 'flex',
    width: '100%',
  },
});
